import find from 'lodash/find';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLagoon } from '../useLagoon';

/**
 * Shared hook for filter search configuration.
 * Used by both CheckBoxGroup and RadioGroup to determine
 * whether search is enabled, the threshold, and the no-results message.
 */
export function useFilterSearchConfig(optionsCount: number) {
  const { t } = useTranslation();

  const [enableFilterSearch] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_FILTER_SEARCH,
  ]);

  const allConfigs = useLagoon('config');
  const thresholdRaw = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.FILTER_SEARCH_THRESHOLD
  );
  const thresholdTrimmed = String(thresholdRaw ?? '').trim();
  const parsedThreshold =
    thresholdTrimmed === '' ? NaN : Number(thresholdTrimmed);
  const filterSearchThreshold = Number.isFinite(parsedThreshold)
    ? parsedThreshold
    : 10;

  const uiMessagingData = useLagoon('ui-messaging')();
  const noResultsMessage = find(uiMessagingData, {
    key: ConstantsLagoon.CONTENT_FILTERS.FILTER_SEARCH_NO_RESULTS,
  });

  const showSearchInput =
    enableFilterSearch && optionsCount > filterSearchThreshold;

  const getNoResultsMessage = (searchTerm: string) => {
    if (!noResultsMessage?.message) {
      return t('CONTENT_FILTERS.FILTER_SEARCH_NO_RESULTS_DEFAULT', {
        searchTerm,
      });
    }
    return noResultsMessage.message.replace('{searchTerm}', searchTerm);
  };

  return {
    enableFilterSearch,
    filterSearchThreshold,
    showSearchInput,
    getNoResultsMessage,
  };
}
